import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import {
  Loader2,
  LogOut,
  Phone,
  Mail,
  ArrowLeft,
  Trash2,
  Search,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Wallet,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Users,
  ListFilter,

} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { buildWhatsAppUrl } from "@/lib/phone";
import { groupByIdentity, collectEmails } from "@/lib/student-dedupe";
import { useAdminSearch, matchesQuery } from "@/components/admin/AdminSearchContext";
import { SortControl, compareBySort, useAdminSort } from "@/components/admin/SortControl";

import { toast } from "sonner";
import { useAdminDataRefresh } from "@/lib/admin-refresh";
import { syncPaymentsFromSheet } from "@/lib/payments-sync.functions";
import { reconcileRazorpayPayments } from "@/lib/razorpay.functions";


const LAST_PAYMENTS_SYNC_KEY = "welivdit:payments:last-sync";

type Payment = {
  id: string;
  plan_name: string;
  amount_paise: number;
  currency: string;
  status: string;
  buyer_name: string;
  buyer_phone: string | null;
  buyer_email: string | null;
  interview_date: string | null;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  failure_reason: string | null;
  created_at: string;
};


const STATUS_COLOR: Record<string, string> = {
  paid: "bg-emerald-100 text-emerald-900 border-emerald-200",
  failed: "bg-rose-100 text-rose-900 border-rose-200",
  created: "bg-amber-100 text-amber-900 border-amber-200",
};

type Filter = "all" | "razorpay" | "manual" | "failed";

type PaymentSource = "razorpay" | "manual";

type ResolvedPayment = Payment & {
  /** Merged group members (all rows that resolved to this master). */
  merged: Payment[];
  /** razorpay if any paid row has a real gateway order id; else manual. */
  source: PaymentSource;
  /** Effective status after failed-then-paid resolution. */
  resolvedStatus: "paid" | "failed" | "created";
  /** Every email seen across merged rows (primary first). */
  emails: string[];
  /** Earliest payment date across merged rows (used for month grouping). */
  firstPaidAt: string;
};

function isManualOrderId(id: string | null | undefined): boolean {
  if (!id) return true;
  return id.startsWith("manual_") || id.startsWith("manual_intake_");
}

/**
 * Collapse duplicate submissions from the sheet + gateway into one master row.
 * Rows are unioned across ANY shared identity (email, phone, or full name), so
 * a WhatsApp-era manual record and a website Razorpay record for the same
 * student resolve to a single student.
 * - Prefer Razorpay-verified paid row over manual paid row (gateway data wins).
 * - If any variant is 'paid', master status = 'paid'.
 * - Keeps the LATEST field values from the most recent row.
 */
function resolvePayments(rows: Payment[]): ResolvedPayment[] {
  const groups = groupByIdentity(rows, (p) => ({
    email: p.buyer_email,
    phone: p.buyer_phone,
    name: p.buyer_name,
    fallbackId: p.id,
  }));

  const out: ResolvedPayment[] = [];
  for (const members of groups) {
    // Newest first
    const sorted = [...members].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const paidRows = sorted.filter((m) => m.status === "paid");
    const razorpayPaid = paidRows.find((m) => !isManualOrderId(m.razorpay_order_id));
    const manualPaid = paidRows.find((m) => isManualOrderId(m.razorpay_order_id));
    // Master selection priority: verified razorpay paid > manual paid > newest overall
    const master = razorpayPaid ?? manualPaid ?? sorted[0]!;

    const resolvedStatus: "paid" | "failed" | "created" =
      paidRows.length > 0
        ? "paid"
        : sorted.some((m) => m.status === "failed")
          ? "failed"
          : "created";

    const source: PaymentSource =
      resolvedStatus === "paid" && razorpayPaid ? "razorpay" : isManualOrderId(master.razorpay_order_id) ? "manual" : "razorpay";

    // Merge latest non-empty fields from newest rows (email/phone/name/plan).
    const latest = { ...master };
    for (const m of sorted) {
      if (!latest.buyer_email && m.buyer_email) latest.buyer_email = m.buyer_email;
      if (!latest.buyer_phone && m.buyer_phone) latest.buyer_phone = m.buyer_phone;
      if (!latest.interview_date && m.interview_date) latest.interview_date = m.interview_date;
      if (!latest.failure_reason && m.failure_reason) latest.failure_reason = m.failure_reason;
    }

    // Primary email = the master (gateway-preferred) row's email.
    const emails = collectEmails([latest.buyer_email, ...sorted.map((m) => m.buyer_email)]);
    const relevant = paidRows.length ? paidRows : sorted;
    const firstPaidAt = relevant.reduce(
      (min, m) => (m.created_at < min ? m.created_at : min),
      relevant[0]!.created_at,
    );

    out.push({ ...latest, merged: sorted, source, resolvedStatus, emails, firstPaidAt });
  }
  return out.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

export const Route = createFileRoute("/admin/payments")({
  validateSearch: (s: Record<string, unknown>) => {
    const c = s.category;
    return {
      category: c === "readiness" ? ("readiness" as const) : ("mocks" as const),
    };
  },
  head: () => ({
    meta: [
      { title: "Payments - Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPaymentsPage,
});

function AdminPaymentsPage() {
  const navigate = useNavigate();
  const { category } = Route.useSearch();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [rows, setRows] = useState<Payment[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useAdminSort("payments");
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [, forceTick] = useState(0);
  const runSyncPayments = useServerFn(syncPaymentsFromSheet);
  const runReconcile = useServerFn(reconcileRazorpayPayments);
  const [reconciling, setReconciling] = useState(false);

  const [viewAll, setViewAll] = useState(false);
  const [page, setPage] = useState(0);
  const [tableSearch, setTableSearch] = useState("");
  const [detail, setDetail] = useState<ResolvedPayment | null>(null);
  const [detailExtra, setDetailExtra] = useState<{
    intake: Record<string, unknown> | null;
    milestone: Record<string, unknown> | null;
    loading: boolean;
  }>({ intake: null, milestone: null, loading: false });

  // Load the student's intake form + mock-slot milestones for the detail drawer.
  useEffect(() => {
    if (!detail) return;
    let active = true;
    setDetailExtra({ intake: null, milestone: null, loading: true });
    (async () => {
      const email = (detail.buyer_email ?? "").toLowerCase().trim();
      const [intakeRes, msRes] = await Promise.all([
        email
          ? supabase.from("client_intakes").select("*").ilike("email", email).maybeSingle()
          : Promise.resolve({ data: null }),
        supabase.from("admin_student_milestones").select("*").eq("payment_id", detail.id).maybeSingle(),
      ]);
      if (!active) return;
      setDetailExtra({
        intake: (intakeRes.data as Record<string, unknown> | null) ?? null,
        milestone: (msRes.data as Record<string, unknown> | null) ?? null,
        loading: false,
      });
    })();
    return () => {
      active = false;
    };
  }, [detail]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleReconcile = useCallback(async () => {

    if (reconciling) return;
    setReconciling(true);
    try {
      const res = await runReconcile();
      toast.success(
        `Checked ${res.checked} pending order${res.checked === 1 ? "" : "s"} · ${res.recovered} recovered as paid`,
      );
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Reconcile failed");
    } finally {
      setReconciling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciling, runReconcile]);


  useEffect(() => {
    try {
      const v = localStorage.getItem(LAST_PAYMENTS_SYNC_KEY);
      if (v) setLastSyncedAt(v);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("payments")
      .select(
        "id, plan_name, amount_paise, currency, status, buyer_name, buyer_phone, buyer_email, interview_date, razorpay_order_id, razorpay_payment_id, failure_reason, created_at",
      )

      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Couldn't load payments");
      return;
    }
    setRows((data ?? []) as Payment[]);
  }, []);

  useAdminDataRefresh(load);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/payments" } });
        return;
      }
      if (!active) return;
      if (!(await hasAdminAccess(sess.session))) {
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      await load();
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [navigate, load]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  const handleSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await runSyncPayments();
      const ts = res.syncedAt ?? new Date().toISOString();
      setLastSyncedAt(ts);
      try {
        localStorage.setItem(LAST_PAYMENTS_SYNC_KEY, ts);
        localStorage.setItem("welivdit:intake:last-sync", ts);
      } catch {
        /* ignore */
      }
      await load();
      const errSuffix = res.errors?.length ? ` · ${res.errors.length} error(s)` : "";
      toast.success(
        `Synced ${res.upserted} paid client${res.upserted === 1 ? "" : "s"} from sheet${errSuffix}`,
      );
      if (res.errors?.length) {
        console.warn("payments sync errors:", res.errors);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Sync failed");
      console.error(e);
    } finally {
      setSyncing(false);
    }
  }, [syncing, runSyncPayments, load]);

  const handleDelete = async (p: ResolvedPayment) => {
    const extra = p.merged.length > 1 ? ` (and ${p.merged.length - 1} merged duplicate row${p.merged.length - 1 === 1 ? "" : "s"})` : "";
    if (
      !window.confirm(
        `Revoke payment record for ${p.buyer_name} (${p.plan_name})${extra}?\nThis cannot be undone.`,
      )
    )
      return;
    const ids = p.merged.map((m) => m.id);
    const prev = rows;
    setRows((r) => r.filter((x) => !ids.includes(x.id)));
    const { error } = await supabase.from("payments").delete().in("id", ids);
    if (error) {
      setRows(prev);
      toast.error("Couldn't revoke payment");
    } else {
      toast.success("Payment revoked");
    }
  };

  const { query } = useAdminSearch();

  // Resolve raw payment rows into unique master profiles.
  const allResolved = useMemo(() => resolvePayments(rows), [rows]);
  const resolved = useMemo(() => {
    return allResolved.filter((r) => {
      const isReadiness = (r.plan_name ?? "").toLowerCase().includes("readiness");
      return category === "readiness" ? isReadiness : !isReadiness;
    });
  }, [allResolved, category]);

  const counts = useMemo(() => {
    const successful = resolved.filter((r) => r.resolvedStatus === "paid");
    return {
      all: successful.length,
      razorpay: successful.filter((r) => r.source === "razorpay").length,
      manual: successful.filter((r) => r.source === "manual").length,
      failed: resolved.filter((r) => r.resolvedStatus === "failed").length,
      totalUnique: resolved.length,
    };
  }, [resolved]);

  const revenue = useMemo(
    () =>
      resolved
        .filter((r) => r.resolvedStatus === "paid")
        .reduce((s, r) => s + (r.amount_paise || 0), 0) / 100,
    [resolved],
  );

  /**
   * Seasonal split across BOTH enrollment eras (deduplicated students only):
   *  - Historical  → manual/WhatsApp-era enrollments (manual order id or a
   *    payment recorded before 3 Jun 2026, when the website went live).
   *  - Fall 2026   → historical enrollments with an interview on/before
   *    31 Aug 2026 (or no date yet), PLUS website/Razorpay payments made on or
   *    after 1 Jun 2026 with an interview on/before 31 Aug 2026.
   *  - Spring 2027 → any student whose interview date is on/after 15 Sep 2026.
   */
  const seasons = useMemo(() => {
    const paidRows = resolved.filter((r) => r.resolvedStatus === "paid");
    let fallRevenue = 0;
    let springRevenue = 0;
    let fallStudents = 0;
    let springStudents = 0;
    let historicalRevenue = 0;
    let historicalStudents = 0;
    let websiteRevenue = 0;
    let websiteStudents = 0;

    for (const r of paidRows) {
      const amount = (r.amount_paise || 0) / 100;
      const interview = String((r as { interview_date?: string | null }).interview_date ?? "").slice(0, 10);
      const paidOn = String((r as { created_at?: string | null }).created_at ?? "").slice(0, 10);
      const isHistorical = r.source === "manual" || (!!paidOn && paidOn < "2026-06-03");

      if (isHistorical) {
        historicalRevenue += amount;
        historicalStudents += 1;
      } else {
        websiteRevenue += amount;
        websiteStudents += 1;
      }

      if (interview && interview >= "2026-09-15") {
        springRevenue += amount;
        springStudents += 1;
      } else if (isHistorical) {
        // WhatsApp-era enrollments belong to the Fall 2026 cohort.
        if (!interview || interview <= "2026-08-31") {
          fallRevenue += amount;
          fallStudents += 1;
        }
      } else if (paidOn >= "2026-06-01" && (!interview || interview <= "2026-08-31")) {
        fallRevenue += amount;
        fallStudents += 1;
      }
    }
    return {
      fallRevenue,
      springRevenue,
      fallStudents,
      springStudents,
      historicalRevenue,
      historicalStudents,
      websiteRevenue,
      websiteStudents,
    };
  }, [resolved]);



  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resolved
      .filter((r) => {
        if (filter === "all") return r.resolvedStatus === "paid";
        if (filter === "razorpay") return r.resolvedStatus === "paid" && r.source === "razorpay";
        if (filter === "manual") return r.resolvedStatus === "paid" && r.source === "manual";
        if (filter === "failed") return r.resolvedStatus === "failed";
        return true;
      })
      .filter((r) => {
        if (!q) return true;
        return (
          r.buyer_name?.toLowerCase().includes(q) ||
          r.emails.some((em) => em.includes(q)) ||
          r.buyer_phone?.toLowerCase().includes(q) ||
          r.plan_name?.toLowerCase().includes(q) ||
          r.razorpay_order_id?.toLowerCase().includes(q) ||
          r.razorpay_payment_id?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => compareBySort({ name: a.buyer_name, createdAt: a.created_at }, { name: b.buyer_name, createdAt: b.created_at }, sort));
  }, [resolved, filter, query, sort]);

  // Local table search (drives the expanded "View All Payments" mode).
  const searched = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter(
      (r) =>
        r.buyer_name?.toLowerCase().includes(q) ||
        r.buyer_email?.toLowerCase().includes(q) ||
        r.buyer_phone?.toLowerCase().includes(q),
    );
  }, [filtered, tableSearch]);

  const PER_PAGE = 15;
  const pageCount = Math.max(1, Math.ceil(searched.length / PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const visibleRows = useMemo(
    () => (viewAll ? searched.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE) : searched.slice(0, 8)),
    [viewAll, searched, safePage],
  );

  useEffect(() => {
    setPage(0);
  }, [tableSearch, filter, viewAll]);

  const exportCsv = () => {
    const header = [
      "Created",
      "Status",
      "Source",
      "Buyer",
      "Phone",
      "Email",
      "Plan",
      "Amount (INR)",
      "Order ID",
      "Payment ID",
      "Interview Date",
    ];
    const lines = [header.join(",")];
    for (const r of filtered) {
      const cells = [
        format(new Date(r.created_at), "yyyy-MM-dd HH:mm"),
        r.resolvedStatus,
        r.source,
        r.buyer_name,
        r.buyer_phone ?? "",
        r.buyer_email ?? "",
        r.plan_name,
        ((r.amount_paise || 0) / 100).toString(),
        r.razorpay_order_id ?? "",
        r.razorpay_payment_id ?? "",
        r.interview_date ?? "",
      ].map((v) => {
        let str = String(v);
        // Neutralize spreadsheet formula injection.
        if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
        return `"${str.replace(/"/g, '""')}"`;
      });
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments-${filter}-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (!authorized)
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl">Not authorized</h1>
          <p className="mt-2 text-muted-foreground">Your account doesn't have admin access.</p>
          <Button variant="outline" onClick={handleSignOut} className="mt-4">
            Sign out
          </Button>
        </div>
      </div>
    );

  return (
    <AdminShell
      title={category === "readiness" ? "Visa Readiness Payments" : "Mock Payments"}
      description={
        category === "readiness"
          ? "AI Visa Readiness Assessment purchases (₹499 / ₹999)."
          : "Basic, Full & Refusal Support mock-interview payments."
      }
      actions={
        <div className="flex flex-col items-stretch md:items-end gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground"
            aria-live="polite"
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                syncing
                  ? "bg-amber-500 animate-pulse"
                  : lastSyncedAt
                    ? "bg-emerald-500"
                    : "bg-muted-foreground/50"
              }`}
              aria-hidden="true"
            />
            {syncing
              ? "Syncing from sheet…"
              : lastSyncedAt
                ? `Sheet synced ${formatDistanceToNow(parseISO(lastSyncedAt), { addSuffix: true })}`
                : "Sheet not synced yet"}
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void handleSync()} disabled={syncing} className="gap-2">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {syncing ? "Syncing" : "Sync from Sheet"}
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleReconcile()}
              disabled={reconciling}
              className="gap-2"
            >
              {reconciling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {reconciling ? "Checking Razorpay" : "Reconcile Razorpay"}
            </Button>

            <SortControl value={sort} onChange={setSort} label="Sort payments" />
            <Button variant="outline" onClick={exportCsv} className="gap-2">
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button variant="outline" onClick={handleSignOut} className="gap-2">
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      }
    >


      {/* Headline metrics */}
      <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Total Revenue (Lifetime)
            </span>
            <Wallet className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="mt-2 font-display text-3xl font-bold tabular-nums">
            ₹{revenue.toLocaleString("en-IN")}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            manual WhatsApp era + website enrollments, deduplicated
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
              Pre 3 Jun · ₹{seasons.historicalRevenue.toLocaleString("en-IN")} · {seasons.historicalStudents}
            </span>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              Website · ₹{seasons.websiteRevenue.toLocaleString("en-IN")} · {seasons.websiteStudents}
            </span>
          </div>

        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Seasonal Breakdown
            </span>
            <Wallet className="h-4 w-4 text-amber-500" />
          </div>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between rounded-xl bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
              <span className="text-[11px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                Fall 2026
              </span>
              <span className="font-display text-sm font-bold tabular-nums text-amber-900 dark:text-amber-100">
                ₹{seasons.fallRevenue.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-sky-50 px-3 py-2 dark:bg-sky-950/30">
              <span className="text-[11px] font-bold uppercase tracking-wide text-sky-800 dark:text-sky-200">
                Spring 2027
              </span>
              <span className="font-display text-sm font-bold tabular-nums text-sky-900 dark:text-sky-100">
                ₹{seasons.springRevenue.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Fall = WhatsApp-era enrollments + website payments from 1 Jun 2026 with interviews up to 31 Aug 2026.
            Interviews on/after 15 Sep 2026 auto-route to Spring 2027.
          </p>

        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Paid Students</span>
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-2 font-display text-3xl font-bold tabular-nums">{counts.all}</div>
          <p className="mt-0.5 text-xs text-muted-foreground">unique students after deduplication</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              Fall 2026 · {seasons.fallStudents}
            </span>
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
              Spring 2027 · {seasons.springStudents}
            </span>
          </div>
        </div>
      </div>


      {/* Status summary tabs */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            key: "all" as Filter,
            label: "All Successful",
            value: counts.all,
            icon: Wallet,
            color: "text-foreground",
            ring: "ring-primary",
            hint: `₹${revenue.toLocaleString("en-IN")} collected`,
          },
          {
            key: "razorpay" as Filter,
            label: "💳 Razorpay Verified",
            value: counts.razorpay,
            icon: CheckCircle2,
            color: "text-emerald-600",
            ring: "ring-emerald-500",
            hint: "Gateway-verified transactions",
          },
          {
            key: "manual" as Filter,
            label: "✍️ Manual Payment",
            value: counts.manual,
            icon: Clock,
            color: "text-amber-600",
            ring: "ring-amber-500",
            hint: "From sheet imports",
          },
          {
            key: "failed" as Filter,
            label: "Failed",
            value: counts.failed,
            icon: XCircle,
            color: "text-rose-600",
            ring: "ring-rose-500",
            hint: "No successful attempt",
          },
        ].map((t) => {
          const Icon = t.icon;
          const active = filter === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`text-left rounded-2xl border border-border bg-card p-4 transition hover:border-primary/40 ${
                active ? `ring-2 ${t.ring}` : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t.label}
                </span>
                <Icon className={`h-4 w-4 ${t.color}`} />
              </div>
              <div className="mt-2 font-display text-2xl font-semibold">{t.value}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{t.hint}</div>
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground">
        {counts.totalUnique} unique customer profile{counts.totalUnique === 1 ? "" : "s"} after deduplication
        (duplicate sheet rows &amp; failed-then-paid attempts merged; Razorpay always wins over manual).
      </p>

      {/* Filter hint */}
      {query && (
        <p className="mt-6 text-xs text-muted-foreground">
          Filtering by top-bar search: <span className="font-medium text-foreground">"{query}"</span>
        </p>
      )}


      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">
            {viewAll ? "All Payments" : "Recent Payments"}
          </h2>
          <p className="text-xs text-muted-foreground">
            {viewAll
              ? `${searched.length} record${searched.length === 1 ? "" : "s"} · page ${safePage + 1} of ${pageCount}`
              : `Showing the latest ${Math.min(8, searched.length)} of ${searched.length}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {viewAll && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                placeholder="Search name or email"
                aria-label="Search all payments by student name or email"
                className="w-56 pl-9"
              />
            </div>
          )}
          <Button variant={viewAll ? "default" : "outline"} onClick={() => setViewAll((v) => !v)} className="gap-2">
            <ListFilter className="h-4 w-4" />
            {viewAll ? "Show Recent Only" : "View All Payments"}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            No payments match this view.
          </div>
        )}
        {visibleRows.map((p) => {
          const attempts = p.merged;
          const hasFailedAttempt = attempts.some((m) => m.status === "failed");
          const retrySuccess = p.resolvedStatus === "paid" && hasFailedAttempt;
          const statusLabel =
            p.resolvedStatus === "paid"
              ? retrySuccess
                ? "🟢 Paid (Retry Successful)"
                : "🟢 Paid"
              : p.resolvedStatus === "failed"
                ? "🔴 Failed"
                : "⏳ Pending";
          const open = expanded.has(p.id);
          return (
          <article
            key={p.id}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              const t = e.target as HTMLElement;
              if (t.closest("a,button")) return;
              setDetail(p);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setDetail(p);
              }
            }}
            aria-label={`Open full profile for ${p.buyer_name}`}
            className="cursor-pointer rounded-2xl border border-border bg-card p-5 transition hover:border-primary/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-semibold">{p.buyer_name}</h3>
                  <div className="flex flex-col items-start gap-0.5">
                    <Badge
                      variant="outline"
                      className={STATUS_COLOR[p.resolvedStatus] ?? "bg-muted text-muted-foreground"}
                    >
                      {statusLabel}
                    </Badge>
                    {p.resolvedStatus === "failed" && p.failure_reason && (
                      <span className="text-[11px] text-rose-600 leading-tight max-w-[220px]">
                        {p.failure_reason}
                      </span>
                    )}
                  </div>

                  <Badge
                    variant="outline"
                    className={
                      p.source === "razorpay"
                        ? "bg-emerald-50 text-emerald-900 border-emerald-200"
                        : "bg-amber-50 text-amber-900 border-amber-200"
                    }
                  >
                    {p.source === "razorpay" ? "💳 Razorpay Verified" : "✍️ Manual"}
                  </Badge>
                  <Badge variant="secondary">{p.plan_name}</Badge>
                  <Badge variant="outline">
                    ₹{((p.amount_paise || 0) / 100).toLocaleString("en-IN")}
                  </Badge>
                  {attempts.length > 1 && (
                    <Badge variant="outline" className="bg-muted text-muted-foreground">
                      merged {attempts.length} rows
                    </Badge>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  {p.buyer_phone && (
                    <a
                      href={buildWhatsAppUrl(p.buyer_phone, `Hi ${p.buyer_name || "there"}! This is the WeLivdIt team.`)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <Phone className="h-3.5 w-3.5" /> {p.buyer_phone}
                    </a>
                  )}
                  {p.emails.length > 0 && (
                    <span className="inline-flex flex-col gap-0.5">
                      {p.emails.map((em, i) => (
                        <a
                          key={em}
                          href={`mailto:${em}`}
                          className={
                            "inline-flex items-center gap-1 hover:text-primary " +
                            (i > 0 ? "text-xs opacity-75" : "")
                          }
                        >
                          <Mail className="h-3.5 w-3.5" /> {em}
                          {i > 0 && <span className="text-[10px] uppercase">alt</span>}
                        </a>
                      ))}
                    </span>
                  )}
                  <span>Created {format(new Date(p.created_at), "PP p")}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono break-all">
                  {p.razorpay_order_id && <>order: {p.razorpay_order_id} </>}
                  {p.razorpay_payment_id && <> · pay: {p.razorpay_payment_id}</>}
                </div>

                {attempts.length > 1 && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(p.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                      aria-expanded={open}
                    >
                      {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {open ? "Hide" : "Show"} attempt history ({attempts.length})
                    </button>
                    {open && (
                      <ul className="mt-2 space-y-1.5 rounded-xl border border-border/60 bg-muted/30 p-3">
                        {attempts.map((m) => (
                          <li key={m.id} className="flex flex-wrap items-center gap-2 text-xs">
                            <Badge
                              variant="outline"
                              className={STATUS_COLOR[m.status] ?? "bg-muted text-muted-foreground"}
                            >
                              {m.status === "paid" ? "🟢 Paid" : m.status === "failed" ? "🔴 Failed" : "⏳ Pending"}
                            </Badge>
                            <span className="tabular-nums font-medium">
                              ₹{((m.amount_paise || 0) / 100).toLocaleString("en-IN")}
                            </span>
                            <span className="text-muted-foreground tabular-nums">
                              {format(new Date(m.created_at), "dd MMM yyyy, p")}
                            </span>
                            <span className="text-muted-foreground">
                              {isManualOrderId(m.razorpay_order_id) ? "✍️ Manual" : "💳 Razorpay"}
                            </span>
                            {m.failure_reason && (
                              <span className="text-rose-600">· {m.failure_reason}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(p)}
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 gap-1.5"
              >
                <Trash2 className="h-4 w-4" /> Revoke
              </Button>
            </div>
          </article>
          );
        })}

      </div>

      {viewAll && pageCount > 1 && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={safePage === 0}
            onClick={() => setPage((n) => Math.max(0, n - 1))}
            className="gap-1.5"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <span className="text-xs font-medium text-muted-foreground tabular-nums">
            Page {safePage + 1} of {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((n) => Math.min(pageCount - 1, n + 1))}
            className="gap-1.5"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Student profile slide-over */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-xl">{detail.buyer_name}</SheetTitle>
                <SheetDescription>
                  {detail.plan_name} · {detail.resolvedStatus === "paid" ? "Paid" : detail.resolvedStatus}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6 text-sm">
                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Payment</h3>
                  <dl className="mt-2 grid grid-cols-2 gap-y-2">
                    <dt className="text-muted-foreground">Amount</dt>
                    <dd className="font-semibold tabular-nums">
                      ₹{((detail.amount_paise || 0) / 100).toLocaleString("en-IN")}
                    </dd>
                    <dt className="text-muted-foreground">Plan tier</dt>
                    <dd className="font-semibold">{detail.plan_name}</dd>
                    <dt className="text-muted-foreground">Source</dt>
                    <dd className="font-semibold">{detail.source === "razorpay" ? "Razorpay" : "Manual"}</dd>
                    <dt className="text-muted-foreground">Order ID</dt>
                    <dd className="break-all font-mono text-xs">{detail.razorpay_order_id ?? "—"}</dd>
                    <dt className="text-muted-foreground">Transaction ID</dt>
                    <dd className="break-all font-mono text-xs">{detail.razorpay_payment_id ?? "—"}</dd>
                    <dt className="text-muted-foreground">Created</dt>
                    <dd>{format(new Date(detail.created_at), "PP p")}</dd>
                    <dt className="text-muted-foreground">Attempts</dt>
                    <dd>{detail.merged.length}</dd>
                  </dl>
                </section>

                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Contact</h3>
                  <dl className="mt-2 grid grid-cols-2 gap-y-2">
                    <dt className="text-muted-foreground">Email</dt>
                    <dd className="break-all">
                      {detail.emails.length ? (
                        detail.emails.map((em, i) => (
                          <div key={em} className={i > 0 ? "text-xs text-muted-foreground" : ""}>
                            {em}
                            {i > 0 ? " (alt)" : ""}
                          </div>
                        ))
                      ) : (
                        "—"
                      )}
                    </dd>
                    <dt className="text-muted-foreground">Phone</dt>
                    <dd>{detail.buyer_phone ?? "—"}</dd>
                    <dt className="text-muted-foreground">Visa interview</dt>
                    <dd>{detail.interview_date ?? "Not scheduled"}</dd>
                  </dl>
                </section>

                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Mock slots</h3>
                  {detailExtra.loading ? (
                    <p className="mt-2 text-muted-foreground">Loading…</p>
                  ) : detailExtra.milestone ? (
                    <dl className="mt-2 grid grid-cols-2 gap-y-2">
                      <dt className="text-muted-foreground">Mocks requested</dt>
                      <dd>{String(detailExtra.milestone.mocks_requested ?? 0)}</dd>
                      <dt className="text-muted-foreground">Mocks done</dt>
                      <dd>{String(detailExtra.milestone.mocks_done ?? 0)}</dd>
                      <dt className="text-muted-foreground">DS-160 review</dt>
                      <dd>{detailExtra.milestone.ds160_review_done ? "Done" : "Pending"}</dd>
                      <dt className="text-muted-foreground">Ready for interview</dt>
                      <dd>{detailExtra.milestone.ready_for_interview ? "Yes" : "No"}</dd>
                    </dl>
                  ) : (
                    <p className="mt-2 text-muted-foreground">No mock slots assigned yet.</p>
                  )}
                </section>

                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Intake form</h3>
                  {detailExtra.loading ? (
                    <p className="mt-2 text-muted-foreground">Loading…</p>
                  ) : detailExtra.intake ? (
                    <dl className="mt-2 grid grid-cols-2 gap-y-2">
                      {(
                        [
                          ["University", "university"],
                          ["Program", "program"],
                          ["Target degree", "target_degree"],
                          ["Intake term", "intake_term"],
                          ["Consulate", "target_consulate"],
                          ["Biometrics", "biometrics_date"],
                          ["Visa date", "visa_date"],
                          ["WhatsApp", "whatsapp"],
                        ] as const
                      ).map(([label, key]) => (
                        <div key={key} className="contents">
                          <dt className="text-muted-foreground">{label}</dt>
                          <dd>{String(detailExtra.intake?.[key] ?? "—")}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="mt-2 text-muted-foreground">No intake form submitted yet.</p>
                  )}
                </section>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AdminShell>
  );
}
